import {observeCriticalWorkflowInteraction} from '../useCriticalWorkflowTrigger';

export type MetricSelectionPickerVariant = 'history_keys' | 'metric_search';

const EDIT_PANEL_METRIC_SELECTION_WINDOW_DURATION_MS = 2_000;

interface ObserveMetricSelectionPainWindowOptions {
  axis: 'x' | 'y';
  windowDurationMs?: number;
  event: unknown;
  initialSelection: readonly string[];
  nextSelection: readonly string[];
  pickerVariant: MetricSelectionPickerVariant;
}

/**
 * Converts an already-normalized picker update into the shared pain-window
 * contract. Picker components remain responsible for their domain rules, such
 * as histogram exclusivity, so this layer never observes a value that product
 * code would reject or rewrite before committing.
 */
export function observeMetricSelectionPainWindow({
  axis,
  windowDurationMs,
  event,
  initialSelection,
  nextSelection,
  pickerVariant,
}: ObserveMetricSelectionPainWindowOptions): boolean {
  const initialSelectionSet = new Set(initialSelection);
  const nextSelectionSet = new Set(nextSelection);
  let addedMetricCount = 0;
  let removedMetricCount = 0;

  for (const metric of nextSelectionSet) {
    if (!initialSelectionSet.has(metric)) {
      addedMetricCount += 1;
    }
  }
  for (const metric of initialSelectionSet) {
    if (!nextSelectionSet.has(metric)) {
      removedMetricCount += 1;
    }
  }

  if (addedMetricCount === 0 && removedMetricCount === 0) {
    return false;
  }

  const mutation =
    addedMetricCount > 0 && removedMetricCount > 0
      ? 'replace'
      : addedMetricCount > 0
        ? 'add'
        : 'remove';

  return observeCriticalWorkflowInteraction({
    workflow: `edit-panel-${axis}-metric-selection-changed`,
    windowDurationMs:
      windowDurationMs ?? EDIT_PANEL_METRIC_SELECTION_WINDOW_DURATION_MS,
    event,
    workflowContext: {
      pickerVariant,
      mutation,
      addedMetricCount,
      removedMetricCount,
      selectionCount: nextSelectionSet.size,
    },
  });
}
